import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Minus, ShoppingCart, X } from 'lucide-react';
import StoreLayout, { PlaceholderImage, WhatsAppFloat, PriceDisplay, resolveImageSrc } from '../../components/store/StoreLayout';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'sonner';
import { formatRupee as fmt } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [primaryIdx, setPrimaryIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/store/products/${productId}`)
      .then(r => setP(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Product not available'))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <StoreLayout><div className="max-w-7xl mx-auto px-4 py-16 text-stone-500">Loading…</div></StoreLayout>;
  if (err) return <StoreLayout><div className="max-w-7xl mx-auto px-4 py-16 text-center">
    <div className="text-red-700 mb-2">{err}</div>
    <Link to="/shop" className="text-amber-800 text-sm">← Back to Shop</Link>
  </div></StoreLayout>;

  const images = p.image_urls && p.image_urls.length > 0 ? p.image_urls : [];
  const primary = images[primaryIdx] ? resolveImageSrc(images[primaryIdx]) : null;
  const total = qty * p.selling_price;

  const handleAdd = () => {
    addToCart(p, qty);
    toast.success(`${p.product_name} added to cart`, { description: `Qty: ${qty}` });
  };
  const handleBuyNow = () => {
    addToCart(p, qty);
    navigate('/cart');
  };

  return (
    <StoreLayout>
      <WhatsAppFloat />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-800" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4" /> Back to Shop
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-16 grid md:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-xl overflow-hidden bg-stone-50 border border-stone-200 relative group">
            {primary
              ? <img src={primary} alt={p.product_name} className="w-full h-full object-cover cursor-zoom-in" onClick={() => setLightboxOpen(true)} data-testid="pd-primary-image" />
              : <PlaceholderImage className="w-full h-full" />}
            {images.length > 1 && (
              <>
                <button onClick={() => setPrimaryIdx((primaryIdx - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow" aria-label="Previous">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setPrimaryIdx((primaryIdx + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow" aria-label="Next">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2" data-testid="pd-thumbnails">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setPrimaryIdx(i)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition ${i === primaryIdx ? 'border-amber-600' : 'border-transparent hover:border-stone-300'}`}
                >
                  <img src={resolveImageSrc(src)} alt={`${p.product_name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="text-xs text-stone-500 uppercase tracking-widest">{p.product_brand} · {p.category}</div>
          <h1 className="text-3xl md:text-4xl font-semibold mt-1" style={{ fontFamily: '"Playfair Display", serif' }}>{p.product_name}</h1>
          <div className="text-sm text-stone-500 mt-2">
            {[p.product_size, p.product_color].filter(x => x && x !== 'NA').join(' · ')}
          </div>
          <div className="mt-6">
            <PriceDisplay mrp={p.mrp} selling={p.selling_price} unit={p.unit} size="lg" />
          </div>
          <p className="text-xs text-stone-500 mt-1">Delivery + payment discussed on order confirmation</p>

          {/* Qty stepper */}
          <div className="mt-8 flex items-center gap-4">
            <div className="inline-flex items-center border border-stone-300 rounded-full overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:bg-stone-100" data-testid="qty-dec" aria-label="Decrement"><Minus className="w-4 h-4" /></button>
              <span className="px-4 tabular-nums font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2.5 hover:bg-stone-100" data-testid="qty-inc" aria-label="Increment"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-stone-600">Subtotal: <span className="font-semibold text-stone-900">{fmt(total)}</span></div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={handleAdd}
              data-testid="add-to-cart-btn"
              className="inline-flex items-center gap-2 border border-amber-700 text-amber-800 hover:bg-amber-50 px-6 py-3 rounded-full text-sm font-medium transition"
            >
              <ShoppingCart className="w-4 h-4" /> Add to cart
            </button>
            <button
              onClick={handleBuyNow}
              data-testid="buy-now-btn"
              className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-full text-sm font-medium transition"
            >
              Buy now
            </button>
          </div>

          <div className="mt-10 border-t border-stone-200 pt-6 text-sm text-stone-600 space-y-2">
            <p><span className="font-medium text-stone-800">Fulfilled by our trusted vendors.</span> Once you place an order, we&apos;ll reach out to confirm delivery charges and preferred payment.</p>
            <p>For custom sizes, bulk orders, or bespoke joinery — <Link to="/get-quote" className="text-amber-800 underline">request a custom quote</Link>.</p>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && primary && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
          data-testid="pd-lightbox"
        >
          <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 text-white hover:text-amber-300 p-2" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
          <img src={primary} alt={p.product_name} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </StoreLayout>
  );
}
